import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../AuthContext";
import EventRoleChips from "../Components/EventRoleChips";
import { supabase } from "../supabaseClient";
import { formatTime } from "../formatTime";

// Turn a file name like "Amazing Grace (Key of G).pdf" into a safer storage
// name like "amazing-grace-key-of-g". This avoids spaces and unusual symbols.
function cleanFileName(name) {
  return name
    .toLowerCase()
    .replace(/\.pdf$/i, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

// Supabase Edge Function errors can contain useful details in the HTTP response.
// This helper tries to pull out the clearest message for the user.
async function getFunctionErrorMessage(error) {
  if (error?.context instanceof Response) {
    try {
      const body = await error.context.json();

      if (body?.error) {
        return body.error;
      }
    } catch {
      try {
        const text = await error.context.text();

        if (text) {
          return text;
        }
      } catch {
        // Fall back to the Supabase client error message below.
      }
    }
  }

  return error?.message || "Unexpected Edge Function error.";
}

function prefersPdfViewerWindow() {
  const userAgent = navigator.userAgent || "";

  return /Android|iPhone|iPad|iPod/i.test(userAgent);
}

function openPdfDownloadWindow() {
  if (!prefersPdfViewerWindow()) {
    return null;
  }

  return window.open("", "_blank");
}

function openPdfUrl(url, viewerWindow = null) {
  if (viewerWindow && !viewerWindow.closed) {
    viewerWindow.location.href = url;
    return;
  }

  if (prefersPdfViewerWindow()) {
    window.location.href = url;
    return;
  }

  window.open(url, "_blank", "noopener,noreferrer");
}

function downloadBase64Pdf(base64Data, fileName, viewerWindow = null) {
  const binary = atob(base64Data);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);

  if (viewerWindow && !viewerWindow.closed) {
    viewerWindow.location.href = url;
    window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    return;
  }

  const link = document.createElement("a");

  link.href = url;
  link.download = fileName || "setlist.pdf";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const songKeyOptions = [
  "C",
  "C#/Db",
  "D",
  "Eb",
  "E",
  "F",
  "F#/Gb",
  "G",
  "Ab",
  "A",
  "Bb",
  "B",
];
const customKeyValue = "__custom_key__";
const eventRoleOptions = [
  "Worship Leader",
  "Lead Vocal",
  "Backing Vocal",
  "Acoustic Guitar",
  "Electric Guitar",
  "Bass",
  "Keys/Piano",
  "Drums",
  "Percussion",
  "Violin",
  "Sound",
  "Slides/Media",
];

function isPresetSongKey(songKey) {
  return songKeyOptions.includes(songKey);
}

function isAudioFile(file) {
  const audioExtensions = [".aac", ".aiff", ".m4a", ".mp3", ".ogg", ".wav", ".webm"];
  const lowerName = file.name.toLowerCase();

  return (
    file.type.startsWith("audio/") ||
    audioExtensions.some((extension) => lowerName.endsWith(extension))
  );
}

function SongResourceAudio({ resource }) {
  const [audioUrl, setAudioUrl] = useState("");
  const [audioError, setAudioError] = useState("");

  useEffect(() => {
    let ignoreResponse = false;

    async function loadAudioUrl() {
      const { data, error } = await supabase.functions.invoke("r2-song-files", {
        body: {
          action: "song-resource-audio-signed-url",
          resourceId: resource.id,
        },
      });

      if (ignoreResponse) {
        return;
      }

      if (error) {
        setAudioUrl("");
        setAudioError(await getFunctionErrorMessage(error));
        return;
      }

      setAudioError("");
      setAudioUrl(data.signedUrl);
    }

    loadAudioUrl();

    return () => {
      ignoreResponse = true;
    };
  }, [resource.id]);

  if (audioError) {
    return <p className="song-resource-muted">{audioError}</p>;
  }

  if (!audioUrl) {
    return <p className="song-resource-muted">Loading audio...</p>;
  }

  return (
    <audio controls src={audioUrl}>
      Your browser does not support the audio element.
    </audio>
  );
}

function SongResourceSheet({ resource }) {
  const [sheetUrl, setSheetUrl] = useState("");
  const [sheetError, setSheetError] = useState("");

  useEffect(() => {
    let ignoreResponse = false;

    async function loadSheetUrl() {
      const { data, error } = await supabase.functions.invoke("r2-song-files", {
        body: { action: "song-resource-sheet-signed-url", resourceId: resource.id },
      });

      if (ignoreResponse) return;
      if (error) {
        setSheetUrl("");
        setSheetError(await getFunctionErrorMessage(error));
        return;
      }

      setSheetError("");
      setSheetUrl(data.signedUrl);
    }

    loadSheetUrl();
    return () => {
      ignoreResponse = true;
    };
  }, [resource.id]);

  if (sheetError) return <p className="song-resource-muted">{sheetError}</p>;
  if (!sheetUrl) return <p className="song-resource-muted">Loading sheet...</p>;

  return (
    <a className="song-resource-file-link" href={sheetUrl} rel="noopener noreferrer" target="_blank">
      Open PDF
    </a>
  );
}

// Library is the protected My Library page.
// Signed-in users can upload, open, edit, and delete their own PDF songs.
function Library() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const requestedEventId = searchParams.get("event");

  // songs holds the rows loaded from the Supabase "songs" table.
  const [songs, setSongs] = useState([]);
  const [songResources, setSongResources] = useState({});
  const [friendships, setFriendships] = useState([]);
  const [activeShelf, setActiveShelf] = useState("mine");
  const [expandedSongId, setExpandedSongId] = useState(null);

  // title and file track the upload form inputs.
  const [title, setTitle] = useState("");
  const [songKey, setSongKey] = useState("");
  const [customSongKey, setCustomSongKey] = useState("");
  const [file, setFile] = useState(null);

  // These states only affect how the current song list is displayed.
  // They do not change anything in the database.
  const [searchQuery, setSearchQuery] = useState("");
  const [friendFilter, setFriendFilter] = useState("all");
  const [sortMode, setSortMode] = useState("key");

  // These states control the inline edit form for a song row.
  const [editingSongId, setEditingSongId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editSongKey, setEditSongKey] = useState("");
  const [customEditSongKey, setCustomEditSongKey] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  // loadingSongs controls the "Loading songs..." message.
  const [loadingSongs, setLoadingSongs] = useState(true);

  // submitting prevents duplicate uploads while one upload is already running.
  const [submitting, setSubmitting] = useState(false);
  const [copyingSongId, setCopyingSongId] = useState(null);
  const [savingResourceSongId, setSavingResourceSongId] = useState(null);
  const [deletingResourceId, setDeletingResourceId] = useState(null);
  const [resourceForms, setResourceForms] = useState({});
  const [setlistFolders, setSetlistFolders] = useState([]);
  const [setlists, setSetlists] = useState([]);
  const [setlistItems, setSetlistItems] = useState([]);
  const [currentSetlistFolderId, setCurrentSetlistFolderId] = useState(null);
  const [selectedSetlistId, setSelectedSetlistId] = useState(null);
  const [newSetlistFolderName, setNewSetlistFolderName] = useState("");
  const [newSetlistTitle, setNewSetlistTitle] = useState("");
  const [newSetlistDate, setNewSetlistDate] = useState("");
  const [newSetlistNotes, setNewSetlistNotes] = useState("");
  const [setlistSongId, setSetlistSongId] = useState("");
  const [placeholderTitle, setPlaceholderTitle] = useState("");
  const [placeholderBody, setPlaceholderBody] = useState("");
  const [savingSetlistAction, setSavingSetlistAction] = useState(false);
  const [combiningSetlistId, setCombiningSetlistId] = useState(null);
  const [openSetlistFolderIds, setOpenSetlistFolderIds] = useState([]);
  const [setlistCreateMode, setSetlistCreateMode] = useState("");
  const [draggingSetlistItemId, setDraggingSetlistItemId] = useState(null);
  const [expandedEventResourceItemId, setExpandedEventResourceItemId] = useState(null);
  const [eventTab, setEventTab] = useState("overview");
  const [eventDetails, setEventDetails] = useState(null);
  const eventLocationRef = useRef(null);
  const eventNotesRef = useRef(null);
  const [editingEventTitle, setEditingEventTitle] = useState(false);
  const [eventTitleDraft, setEventTitleDraft] = useState("");
  const [eventTeamMembers, setEventTeamMembers] = useState([]);
  const [selectedTeamFriendId, setSelectedTeamFriendId] = useState("");
  const [teamMemberForm, setTeamMemberForm] = useState({
    name_override: "",
    roles: [],
    instrument_part: "",
    needs_gear: "",
    contact_notes: "",
  });

  // message and error display feedback below the upload form.
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    // Load songs once when the Library page first appears.
    loadSongs();
    loadSetlistWorkspace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedSetlistId) {
      setEventTeamMembers([]);
      return;
    }

    let ignore = false;
    async function loadEventTeam() {
      const { data, error: teamError } = await supabase.rpc(
        "get_setlist_team_members",
        { requested_setlist_id: selectedSetlistId }
      );
      if (ignore) return;
      if (teamError) {
        setError(teamError.message);
        setEventTeamMembers([]);
      } else {
        setEventTeamMembers(data || []);
      }
    }
    loadEventTeam();
    return () => { ignore = true; };
  }, [selectedSetlistId]);

  useEffect(() => {
    const textareas = [eventLocationRef.current, eventNotesRef.current].filter(Boolean);
    if (textareas.length === 0) return undefined;

    const resizeTextareas = () => {
      textareas.forEach((textarea) => {
        textarea.style.height = "auto";
        textarea.style.height = `${textarea.scrollHeight}px`;
      });
    };

    resizeTextareas();
    window.addEventListener("resize", resizeTextareas);
    return () => window.removeEventListener("resize", resizeTextareas);
  }, [eventDetails?.id, eventDetails?.location, eventDetails?.notes, eventTab]);

  useEffect(() => {
    const selectedEvent = activeShelf === "events" && setlists.find((setlist) => setlist.id === selectedSetlistId && setlist.is_event);
    if (!selectedEvent) {
      setEventDetails(null);
      return;
    }
    if (eventDetails?.id === selectedEvent.id) return;
    setEventDetails({
      id: selectedEvent.id,
      title: selectedEvent.title || "",
      event_date: selectedEvent.event_date || "",
      start_time: selectedEvent.start_time?.slice(0, 5) || "",
      end_time: selectedEvent.end_time?.slice(0, 5) || "",
      location: selectedEvent.location || "",
      notes: selectedEvent.notes || "",
      theme: selectedEvent.theme || "",
      flow_notes: selectedEvent.flow_notes || "",
    });
  }, [activeShelf, eventDetails?.id, selectedSetlistId, setlists]);

  useEffect(() => {
    setEventTitleDraft(setlists.find((setlist) => setlist.id === selectedSetlistId)?.title || "");
    setEditingEventTitle(false);
  }, [selectedSetlistId, setlists]);

  useEffect(() => {
    if (!requestedEventId || !setlists.some((setlist) => setlist.id === requestedEventId && setlist.is_event)) return;
    setActiveShelf("events");
    setSelectedSetlistId(requestedEventId);
    setEventTab("overview");
  }, [requestedEventId, setlists]);

  useEffect(() => {
    if (activeShelf !== "events" && activeShelf !== "setlists") return;
    const availableSetlists = setlists.filter((setlist) =>
      activeShelf === "events" ? setlist.is_event : !setlist.is_event
    );
    if (!availableSetlists.some((setlist) => setlist.id === selectedSetlistId)) {
      setSelectedSetlistId(availableSetlists[0]?.id || null);
      if (activeShelf === "events") setEventTab("overview");
    }
  }, [activeShelf, selectedSetlistId, setlists]);

  async function loadSongs() {
    setLoadingSongs(true);
    setError("");

    // RLS decides which rows this user is allowed to see. The shelves below
    // split those visible songs into mine and friends.
    const [songsResult, friendshipsResult] = await Promise.all([
      supabase.from("songs").select("*").order("created_at", { ascending: false }),
      supabase.rpc("list_friendships_with_profiles"),
    ]);

    const { data, error: songsError } = songsResult;
    const { data: friendshipsData, error: friendshipsError } = friendshipsResult;

    if (songsError) {
      setError(songsError.message);
    } else if (friendshipsError) {
      setError(friendshipsError.message);
    } else {
      setSongs(data || []);
      setFriendships(friendshipsData || []);
    }

    setLoadingSongs(false);
  }

  async function loadSetlistWorkspace() {
    const [foldersResult, setlistsResult, itemsResult] = await Promise.all([
      supabase.from("setlist_folders").select("*").order("created_at", { ascending: false }),
      supabase
        .from("setlists")
        .select("*")
        .order("created_at", { ascending: false }),
      supabase
        .from("setlist_items")
        .select("*")
        .order("position", { ascending: true }),
    ]);

    if (foldersResult.error) {
      setError(foldersResult.error.message);
      return;
    }

    if (setlistsResult.error) {
      setError(setlistsResult.error.message);
      return;
    }

    if (itemsResult.error) {
      setError(itemsResult.error.message);
      return;
    }

    setSetlistFolders(foldersResult.data || []);
    setSetlists(setlistsResult.data || []);
    setSetlistItems(itemsResult.data || []);
  }

  useEffect(() => {
    async function loadSongResources() {
      if (songs.length === 0) {
        setSongResources({});
        return;
      }

      const { data, error: resourceError } = await supabase
        .from("song_resources")
        .select("*")
        .order("created_at", { ascending: false });

      if (resourceError) {
        setError(resourceError.message);
        setSongResources({});
        return;
      }

      setSongResources(
        (data || []).reduce((resourceMap, resource) => {
          if (!resourceMap[resource.song_id]) {
            resourceMap[resource.song_id] = [];
          }

          resourceMap[resource.song_id].push(resource);
          return resourceMap;
        }, {})
      );
    }

    loadSongResources();
  }, [songs]);

  async function handleUpload(event) {
    // Prevent the browser from refreshing the page after form submit.
    event.preventDefault();
    setSubmitting(true);
    setError("");
    setMessage("");

    // Basic frontend validation gives quick feedback before calling Supabase.
    if (!file) {
      setError("Choose a PDF before uploading.");
      setSubmitting(false);
      return;
    }

    if (file.type !== "application/pdf") {
      setError("Only PDF files can be uploaded.");
      setSubmitting(false);
      return;
    }

    const savedSongKey =
      songKey === customKeyValue ? customSongKey.trim() : songKey;

    if (!savedSongKey) {
      setError("Choose a key for the song before uploading.");
      setSubmitting(false);
      return;
    }

    // Create an id in the browser so the file path and database row can match.
    const songId = crypto.randomUUID();
    const safeName = cleanFileName(file.name) || "song";

    // Store each user's files inside a folder named with their Supabase user id.
    // The Edge Function checks this too, so users cannot upload into another
    // user's folder by changing frontend code.
    const filePath = `${user.id}/${songId}-${safeName}.pdf`;

    // If the user leaves the title blank, use the PDF file name without ".pdf".
    const songTitle = title.trim() || file.name.replace(/\.pdf$/i, "");

    // FormData is required because a real file is being sent to the Edge Function.
    const formData = new FormData();

    formData.append("action", "upload");
    formData.append("filePath", filePath);
    formData.append("file", file);

    // The Edge Function uploads the PDF to Cloudflare R2.
    // The browser does not receive direct R2 credentials, which keeps them secret.
    const { error: uploadError } = await supabase.functions.invoke(
      "r2-song-files",
      {
        body: formData,
      }
    );

    if (uploadError) {
      setError(`PDF upload failed: ${await getFunctionErrorMessage(uploadError)}`);
      setSubmitting(false);
      return;
    }

    // After the file exists in storage, save the searchable metadata in Supabase.
    const { error: insertError } = await supabase.from("songs").insert({
      id: songId,
      owner_id: user.id,
      title: songTitle,
      song_key: savedSongKey,
      file_path: filePath,
    });

    if (insertError) {
      setError(`Song save failed: ${insertError.message}`);
    } else {
      // Reset the form and reload the list so the new song appears.
      setMessage("Song uploaded.");
      setTitle("");
      setSongKey("");
      setCustomSongKey("");
      setFile(null);
      event.target.reset();
      await loadSongs();
    }

    setSubmitting(false);
  }

  async function openPdf(song) {
    setError("");

    // Ask the Edge Function for a temporary signed URL.
    // This lets the user open the PDF without making the R2 bucket public.
    const { data, error: signedUrlError } = await supabase.functions.invoke(
      "r2-song-files",
      {
        body: {
          action: "signed-url",
          songId: song.id,
        },
      }
    );

    if (signedUrlError) {
      setError(await getFunctionErrorMessage(signedUrlError));
      return;
    }

    // Open the signed URL in a new browser tab.
    window.open(data.signedUrl, "_blank", "noopener,noreferrer");
  }

  async function copySongShareLink(song) {
    const shareUrl = `${window.location.origin}/songs/${song.id}`;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setMessage("Friend-only chart link copied.");
      setError("");
    } catch {
      setError("Could not copy the link. You can share this URL: " + shareUrl);
    }
  }

  async function deleteSong(song) {
    setError("");
    setMessage("");

    // First delete the actual PDF file from R2 through the Edge Function.
    const { error: storageError } = await supabase.functions.invoke(
      "r2-song-files",
      {
        body: {
          action: "delete",
          songId: song.id,
        },
      }
    );

    if (storageError) {
      setError(await getFunctionErrorMessage(storageError));
      return;
    }

    // Then delete the database row from Supabase.
    // Doing both keeps the database and file storage in sync.
    const { error: deleteError } = await supabase
      .from("songs")
      .delete()
      .eq("id", song.id);

    if (deleteError) {
      setError(deleteError.message);
    } else {
      setMessage("Song deleted.");
      await loadSongs();
    }
  }

  async function copySongToMyLibrary(song) {
    if (song.owner_id === user.id) {
      return;
    }

    setCopyingSongId(song.id);
    setError("");
    setMessage("");

    const copiedSongId = crypto.randomUUID();
    const safeName = cleanFileName(song.title || "song") || "song";
    const targetFilePath = `${user.id}/${copiedSongId}-${safeName}.pdf`;

    const { data: copyData, error: copyError } = await supabase.functions.invoke(
      "r2-song-files",
      {
        body: {
          action: "copy-song-file",
          songId: song.id,
          targetFilePath,
        },
      }
    );

    if (copyError) {
      setError(await getFunctionErrorMessage(copyError));
      setCopyingSongId(null);
      return;
    }

    const { error: insertError } = await supabase.from("songs").insert({
      copied_from_song_id: song.id,
      file_path: copyData.filePath,
      id: copiedSongId,
      owner_id: user.id,
      song_key: song.song_key,
      title: song.title,
    });

    if (insertError) {
      setError(insertError.message);
    } else {
      setMessage(`"${song.title}" was added to My Library.`);
      await loadSongs();
    }

    setCopyingSongId(null);
  }

  function getResourceForm(songId) {
    return (
      resourceForms[songId] || {
        audioFile: null,
        body: "",
        sheetFile: null,
        title: "",
        type: "note",
        url: "",
      }
    );
  }

  function updateResourceForm(songId, field, value) {
    setResourceForms((currentForms) => ({
      ...currentForms,
      [songId]: {
        ...getResourceForm(songId),
        [field]: value,
      },
    }));
  }

  async function saveSongResource(song) {
    const resourceForm = getResourceForm(song.id);
    const resourceTitle = resourceForm.title.trim() || (
      resourceForm.type === "sheet"
        ? resourceForm.sheetFile?.name.replace(/\.pdf$/i, "") || ""
        : ""
    );
    const resourceBody = resourceForm.body.trim();
    const resourceUrl = resourceForm.url.trim();

    setError("");
    setMessage("");

    if (song.owner_id !== user.id) {
      setError("Only the song owner can add resources.");
      return;
    }

    if (resourceForm.type === "link" && !resourceUrl) {
      setError("Add a link URL first.");
      return;
    }

    if (resourceForm.type === "note" && !resourceTitle && !resourceBody) {
      setError("Add a note title or note text first.");
      return;
    }

    if (resourceForm.type === "audio" && !resourceForm.audioFile) {
      setError("Choose an audio file first.");
      return;
    }

    if (resourceForm.type === "audio" && !isAudioFile(resourceForm.audioFile)) {
      setError("Please choose an audio file.");
      return;
    }

    if (resourceForm.type === "sheet" && !resourceForm.sheetFile) {
      setError("Choose a PDF music sheet first.");
      return;
    }

    if (resourceForm.type === "sheet" && !/\.pdf$/i.test(resourceForm.sheetFile.name)) {
      setError("Please choose a PDF music sheet.");
      return;
    }

    setSavingResourceSongId(song.id);

    let filePath = "";
    let contentType = "";

    if (resourceForm.type === "audio" || resourceForm.type === "sheet") {
      const resourceId = crypto.randomUUID();
      const file = resourceForm.type === "sheet" ? resourceForm.sheetFile : resourceForm.audioFile;
      const extension = file.name.split(".").pop() || (resourceForm.type === "sheet" ? "pdf" : "audio");
      const safeName = cleanFileName(resourceTitle || song.title || "resource") || "resource";
      filePath = `${user.id}/song-resources/${song.id}/${resourceId}-${safeName}.${extension}`;
      contentType = resourceForm.type === "sheet" ? "application/pdf" : file.type;

      const formData = new FormData();
      formData.append("action", "upload");
      formData.append("filePath", filePath);
      formData.append("file", file);

      const { error: uploadError } = await supabase.functions.invoke(
        "r2-song-files",
        {
          body: formData,
        }
      );

      if (uploadError) {
        setError(`File upload failed: ${await getFunctionErrorMessage(uploadError)}`);
        setSavingResourceSongId(null);
        return;
      }
    }

    const { error: insertError } = await supabase.from("song_resources").insert({
      body: resourceBody,
      content_type: contentType,
      file_path: filePath,
      owner_id: user.id,
      resource_type: resourceForm.type,
      song_id: song.id,
      title: resourceTitle,
      url: resourceUrl,
    });

    if (insertError) {
      setError(`Resource save failed: ${insertError.message}`);
      setSavingResourceSongId(null);
      return;
    }

    setResourceForms((currentForms) => ({
      ...currentForms,
      [song.id]: {
        audioFile: null,
        body: "",
        sheetFile: null,
        title: "",
        type: "note",
        url: "",
      },
    }));
    setMessage("Resource added.");
    setSavingResourceSongId(null);
    await loadSongs();
  }

  async function deleteSongResource(resource) {
    setDeletingResourceId(resource.id);
    setError("");
    setMessage("");

    const { error: storageError } = await supabase.functions.invoke(
      "r2-song-files",
      {
        body: {
          action: "delete-song-resource-file",
          resourceId: resource.id,
        },
      }
    );

    if (storageError) {
      setError(await getFunctionErrorMessage(storageError));
      setDeletingResourceId(null);
      return;
    }

    const { error: deleteError } = await supabase
      .from("song_resources")
      .delete()
      .eq("id", resource.id);

    if (deleteError) {
      setError(deleteError.message);
    } else {
      setMessage("Resource deleted.");
      await loadSongs();
    }

    setDeletingResourceId(null);
  }

  function startEditingSong(song) {
    // Copy the current row values into temporary edit state.
    // The database is not changed until saveSongEdit runs.
    setEditingSongId(song.id);
    setEditTitle(song.title || "");
    setEditSongKey(isPresetSongKey(song.song_key) ? song.song_key : customKeyValue);
    setCustomEditSongKey(isPresetSongKey(song.song_key) ? "" : song.song_key || "");
    setMessage("");
    setError("");
  }

  function cancelEditingSong() {
    // Leave edit mode and clear the temporary form values.
    setEditingSongId(null);
    setEditTitle("");
    setEditSongKey("");
    setCustomEditSongKey("");
  }

  async function saveSongEdit(song) {
    // Trim keeps users from saving a title that only contains spaces.
    const nextTitle = editTitle.trim();

    if (!nextTitle) {
      setError("Song title cannot be blank.");
      return;
    }

    const savedEditSongKey =
      editSongKey === customKeyValue ? customEditSongKey.trim() : editSongKey;

    if (!savedEditSongKey) {
      setError("Choose a key for the song.");
      return;
    }

    setSavingEdit(true);
    setError("");
    setMessage("");

    const { error: updateError } = await supabase
      .from("songs")
      .update({
        title: nextTitle,
        song_key: savedEditSongKey,
      })
      // Both filters matter: id picks the row, owner_id makes the update
      // owner-only even if a shared song is visible in this list.
      .eq("id", song.id)
      .eq("owner_id", user.id);

    if (updateError) {
      setError(updateError.message);
    } else {
      setMessage("Song updated.");
      cancelEditingSong();
      await loadSongs();
    }

    setSavingEdit(false);
  }

  const acceptedFriends = useMemo(
    () =>
      friendships
        .filter((friendship) => friendship.status === "accepted")
        .map((friendship) => ({
          id:
            friendship.requester_id === user.id
              ? friendship.addressee_id
              : friendship.requester_id,
          name:
            friendship.other_display_name ||
            friendship.other_email ||
            "Friend",
        }))
        .sort((firstFriend, secondFriend) =>
          firstFriend.name.localeCompare(secondFriend.name)
        ),
    [friendships, user.id]
  );
  const availableEventFriends = acceptedFriends.filter(
    (friend) => !eventTeamMembers.some((member) => member.user_id === friend.id)
  );
  const isCurrentUserOnEventTeam = eventTeamMembers.some(
    (member) => member.user_id === user.id
  );

  const friendsById = useMemo(
    () =>
      acceptedFriends.reduce((friendMap, friend) => {
        friendMap[friend.id] = friend;
        return friendMap;
      }, {}),
    [acceptedFriends]
  );

  // This prepares the list for the screen: filter by shelf, friend, search, and sort.
  // It does not re-query Supabase.
  const visibleSongs = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return songs
      .filter((song) => {
        const isMine = song.owner_id === user.id;

        if (activeShelf === "mine" && !isMine) {
          return false;
        }

        if (activeShelf === "friends" && isMine) {
          return false;
        }

        if (
          activeShelf === "friends" &&
          friendFilter !== "all" &&
          song.owner_id !== friendFilter
        ) {
          return false;
        }

        if (!query) {
          return true;
        }

        const ownerName = friendsById[song.owner_id]?.name || "";

        return (
          song.title?.toLowerCase().includes(query) ||
          ownerName.toLowerCase().includes(query)
        );
      })
      .sort((firstSong, secondSong) => {
        if (sortMode === "newest") {
          return new Date(secondSong.created_at || 0) - new Date(firstSong.created_at || 0);
        }

        if (sortMode === "oldest") {
          return new Date(firstSong.created_at || 0) - new Date(secondSong.created_at || 0);
        }

        if (sortMode === "key") {
          const firstKey = songKeyOptions.indexOf(firstSong.song_key);
          const secondKey = songKeyOptions.indexOf(secondSong.song_key);
          const firstKeyIndex = firstKey === -1 ? songKeyOptions.length : firstKey;
          const secondKeyIndex = secondKey === -1 ? songKeyOptions.length : secondKey;

          if (firstKeyIndex !== secondKeyIndex) {
            return firstKeyIndex - secondKeyIndex;
          }

          return (firstSong.title || "").localeCompare(secondSong.title || "");
        }

        return (firstSong.title || "").localeCompare(secondSong.title || "");
      });
  }, [activeShelf, friendFilter, friendsById, searchQuery, songs, sortMode, user.id]);

  const mySongsCount = songs.filter((song) => song.owner_id === user.id).length;
  const friendSongsCount = songs.filter((song) => song.owner_id !== user.id).length;
  const isMyShelf = activeShelf === "mine";
  const isFriendsShelf = activeShelf === "friends";
  const isSetlistsShelf = activeShelf === "setlists";
  const isEventsShelf = activeShelf === "events";
  const selectedFriendSongsCount =
    friendFilter === "all"
      ? friendSongsCount
      : songs.filter((song) => song.owner_id === friendFilter).length;
  const copiedSongSourceIds = useMemo(
    () =>
      new Set(
        songs
          .filter((song) => song.owner_id === user.id && song.copied_from_song_id)
          .map((song) => song.copied_from_song_id)
      ),
    [songs, user.id]
  );
  const myLibrarySongs = useMemo(
    () =>
      songs
        .filter((song) => song.owner_id === user.id)
        .sort((firstSong, secondSong) =>
          (firstSong.title || "").localeCompare(secondSong.title || "")
        ),
    [songs, user.id]
  );
  const currentSetlistFolder = setlistFolders.find((folder) => folder.id === currentSetlistFolderId);
  const eventSetlists = setlists.filter((setlist) => setlist.is_event);
  const currentFolderSetlists = (isEventsShelf ? eventSetlists : setlists.filter((setlist) => !setlist.is_event && setlist.folder_id === currentSetlistFolderId))
    .sort((firstSetlist, secondSetlist) => {
      if (firstSetlist.event_date && secondSetlist.event_date) {
        return new Date(secondSetlist.event_date) - new Date(firstSetlist.event_date);
      }

      if (firstSetlist.event_date) {
        return -1;
      }

      if (secondSetlist.event_date) {
        return 1;
      }

      return (firstSetlist.title || "").localeCompare(secondSetlist.title || "");
    });
  const selectedSetlist =
    (isEventsShelf ? eventSetlists : setlists.filter((setlist) => !setlist.is_event)).find((setlist) => setlist.id === selectedSetlistId) ||
    currentFolderSetlists[0] ||
    null;
  const selectedSetlistItems = selectedSetlist
    ? setlistItems
        .filter((item) => item.setlist_id === selectedSetlist.id)
        .sort((firstItem, secondItem) => firstItem.position - secondItem.position)
    : [];
  const isSelectedEventOwner = isEventsShelf && selectedSetlist?.owner_id === user.id;
  const songsById = useMemo(
    () =>
      songs.reduce((songMap, song) => {
        songMap[song.id] = song;
        return songMap;
      }, {}),
    [songs]
  );
  const setlistsCount = setlists.filter((setlist) => !setlist.is_event).length;
  const eventCount = eventSetlists.length;

  function formatSongDate(song) {
    // Some older rows or test data may not have created_at populated.
    if (!song.created_at) {
      return "No date";
    }

    return new Intl.DateTimeFormat("en", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(song.created_at));
  }

  function formatSetlistDate(dateValue) {
    if (!dateValue) {
      return "No date";
    }

    return new Intl.DateTimeFormat("en", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(`${dateValue}T00:00:00`));
  }

  async function createSetlist(event) {
    event.preventDefault();
    const setlistTitle = newSetlistTitle.trim();

    if (!setlistTitle) {
      setError("Name the setlist first.");
      return;
    }

    setSavingSetlistAction(true);
    setError("");
    setMessage("");

    const { data, error: insertError } = await supabase
      .from("setlists")
      .insert({
        event_date: newSetlistDate || null,
        folder_id: isEventsShelf ? null : currentSetlistFolderId,
        is_event: isEventsShelf,
        notes: newSetlistNotes.trim(),
        owner_id: user.id,
        title: setlistTitle,
      })
      .select("*")
      .single();

    if (insertError) {
      setError(insertError.message);
    } else {
      setNewSetlistTitle("");
      setNewSetlistDate("");
      setNewSetlistNotes("");
      setSetlistCreateMode("");
      if (!isEventsShelf && currentSetlistFolderId) {
        setOpenSetlistFolderIds((ids) => ids.includes(currentSetlistFolderId) ? ids : [...ids, currentSetlistFolderId]);
      }
      setSelectedSetlistId(data.id);
      setEventTab("overview");
      setMessage(isEventsShelf ? "Event created." : "Setlist created.");
      await loadSetlistWorkspace();
    }

    setSavingSetlistAction(false);
  }

  async function deleteSetlistFolder(folder) {
    if (!window.confirm(`Delete "${folder.name}" and everything inside it?`)) return;
    setSavingSetlistAction(true);
    setError("");
    setMessage("");
    const { error: deleteError } = await supabase.from("setlist_folders").delete().eq("id", folder.id);
    if (deleteError) {
      setError(deleteError.message);
    } else {
      if (currentSetlistFolderId === folder.id) setCurrentSetlistFolderId(folder.parent_folder_id || null);
      setMessage("Folder deleted.");
      await loadSetlistWorkspace();
    }
    setSavingSetlistAction(false);
  }

  async function createSetlistFolder(event) {
    event.preventDefault();
    const folderName = newSetlistFolderName.trim();
    if (!folderName) {
      setError("Name the folder first.");
      return;
    }
    setSavingSetlistAction(true);
    setError("");
    setMessage("");
    const { error: insertError } = await supabase.from("setlist_folders").insert({
      name: folderName,
      owner_id: user.id,
      parent_folder_id: currentSetlistFolderId,
    });
    if (insertError) {
      setError(insertError.message);
    } else {
      setNewSetlistFolderName("");
      setSetlistCreateMode("");
      setMessage("Folder created.");
      await loadSetlistWorkspace();
    }
    setSavingSetlistAction(false);
  }

  async function saveEventDetails(event) {
    event.preventDefault();
    if (!selectedSetlist || selectedSetlist.owner_id !== user.id || !eventDetails) return;
    if (!eventDetails.title.trim()) {
      setError(isEventsShelf ? "Event name cannot be blank." : "Setlist name cannot be blank.");
      return;
    }

    setSavingSetlistAction(true);
    setError("");
    setMessage("");
    const { error: updateError } = await supabase
      .from("setlists")
      .update({
        title: eventDetails.title.trim(),
        event_date: eventDetails.event_date || null,
        start_time: eventDetails.start_time || null,
        end_time: eventDetails.end_time || null,
        location: eventDetails.location.trim(),
        notes: eventDetails.notes.trim(),
        theme: eventDetails.theme.trim(),
        flow_notes: eventDetails.flow_notes.trim(),
      })
      .eq("id", selectedSetlist.id);

    if (updateError) {
      setError(updateError.message);
    } else {
      setMessage("Event details saved.");
      await loadSetlistWorkspace();
    }
    setSavingSetlistAction(false);
  }

  async function saveEventTitle(event) {
    event.preventDefault();
    const title = eventTitleDraft.trim();
    if (!isSelectedEventOwner || !selectedSetlist) return;
    if (!title) {
      setError("Event name cannot be blank.");
      return;
    }

    setSavingSetlistAction(true);
    setError("");
    setMessage("");
    const { error: updateError } = await supabase
      .from("setlists")
      .update({ title })
      .eq("id", selectedSetlist.id);

    if (updateError) {
      setError(updateError.message);
    } else {
      setSetlists((current) => current.map((setlist) =>
        setlist.id === selectedSetlist.id ? { ...setlist, title } : setlist
      ));
      setEventDetails((current) => current?.id === selectedSetlist.id
        ? { ...current, title }
        : current
      );
      setEditingEventTitle(false);
      setMessage("Event name updated.");
    }
    setSavingSetlistAction(false);
  }

  async function addEventTeamMember(event) {
    event.preventDefault();
    if (!selectedSetlist || selectedSetlist.owner_id !== user.id || !selectedTeamFriendId) {
      return;
    }

    setSavingSetlistAction(true);
    setError("");
    setMessage("");
    const { error: insertError } = await supabase
      .from("setlist_team_members")
      .insert({
        setlist_id: selectedSetlist.id,
        owner_id: user.id,
        user_id: selectedTeamFriendId,
        name_override: teamMemberForm.name_override.trim(),
        roles: teamMemberForm.roles,
        instrument_part: teamMemberForm.instrument_part.trim(),
        needs_gear: teamMemberForm.needs_gear.trim(),
        contact_notes: teamMemberForm.contact_notes.trim(),
        position: eventTeamMembers.length,
      });

    if (insertError) {
      setError(insertError.message);
    } else {
      setSelectedTeamFriendId("");
      setTeamMemberForm({ name_override: "", roles: [], instrument_part: "", needs_gear: "", contact_notes: "" });
      setMessage("Team member added.");
      const { data } = await supabase.rpc("get_setlist_team_members", {
        requested_setlist_id: selectedSetlist.id,
      });
      setEventTeamMembers(data || []);
    }
    setSavingSetlistAction(false);
  }

  async function removeEventTeamMember(member) {
    if (!selectedSetlist || selectedSetlist.owner_id !== user.id) return;
    setSavingSetlistAction(true);
    setError("");
    const { error: deleteError } = await supabase
      .from("setlist_team_members")
      .delete()
      .eq("id", member.id);
    if (deleteError) {
      setError(deleteError.message);
    } else {
      setEventTeamMembers((currentMembers) =>
        currentMembers.filter((currentMember) => currentMember.id !== member.id)
      );
      setMessage(`${member.display_name} removed from the team.`);
    }
    setSavingSetlistAction(false);
  }

  async function saveEventTeamMember(event, member) {
    event.preventDefault();
    if (!isSelectedEventOwner) return;
    const formData = new FormData(event.currentTarget);
    setSavingSetlistAction(true);
    setError("");
    const { error: updateError } = await supabase
      .from("setlist_team_members")
      .update({
        name_override: String(formData.get("name_override") || "").trim(),
        roles: formData.getAll("roles"),
        instrument_part: String(formData.get("instrument_part") || "").trim(),
        needs_gear: String(formData.get("needs_gear") || "").trim(),
        contact_notes: String(formData.get("contact_notes") || "").trim(),
      })
      .eq("id", member.id);
    if (updateError) {
      setError(updateError.message);
    } else {
      const { data } = await supabase.rpc("get_setlist_team_members", {
        requested_setlist_id: selectedSetlist.id,
      });
      setEventTeamMembers(data || []);
      setMessage(`${member.display_name}'s assignment saved.`);
    }
    setSavingSetlistAction(false);
  }

  async function saveEventSongDetails(event, item) {
    event.preventDefault();
    if (!isSelectedEventOwner) return;
    const formData = new FormData(event.currentTarget);
    const fields = [
      "artist_version",
      "tempo_feel",
      "theme_scripture",
      "lead_vocal",
      "other_vocals",
      "arrangement",
      "status",
      "notes_link",
    ];
    const updates = Object.fromEntries(fields.map((field) => [field, String(formData.get(field) || "").trim()]));

    setSavingSetlistAction(true);
    setError("");
    const { error: updateError } = await supabase
      .from("setlist_items")
      .update(updates)
      .eq("id", item.id);
    if (updateError) {
      setError(updateError.message);
    } else {
      setMessage("Song plan updated.");
      await loadSetlistWorkspace();
    }
    setSavingSetlistAction(false);
  }

  async function addSongToSetlist(event) {
    event.preventDefault();

    if (!isSelectedEventOwner) return;

    if (!selectedSetlist) {
      setError("Create or choose a setlist first.");
      return;
    }

    if (!setlistSongId) {
      setError("Choose a song first.");
      return;
    }

    setSavingSetlistAction(true);
    setError("");
    setMessage("");

    const position = selectedSetlistItems.length;
    const { error: insertError } = await supabase.from("setlist_items").insert({
      item_type: "song",
      owner_id: user.id,
      position,
      setlist_id: selectedSetlist.id,
      song_id: setlistSongId,
      title: songsById[setlistSongId]?.title || "",
      song_key_snapshot: songsById[setlistSongId]?.song_key || "",
    });

    if (insertError) {
      setError(insertError.message);
    } else {
      setSetlistSongId("");
      setMessage("Song added to setlist.");
      await loadSetlistWorkspace();
    }

    setSavingSetlistAction(false);
  }

  async function addPlaceholderToSetlist(event) {
    event.preventDefault();

    if (!selectedSetlist) {
      setError("Create or choose a setlist first.");
      return;
    }

    if (selectedSetlist.owner_id !== user.id) return;

    const titleText = placeholderTitle.trim();
    const bodyText = placeholderBody.trim();

    if (!titleText && !bodyText) {
      setError("Add a title or note for the blank page.");
      return;
    }

    setSavingSetlistAction(true);
    setError("");
    setMessage("");

    const position = selectedSetlistItems.length;
    const { error: insertError } = await supabase.from("setlist_items").insert({
      body: bodyText,
      item_type: "placeholder",
      owner_id: user.id,
      position,
      setlist_id: selectedSetlist.id,
      title: titleText || "Blank page",
    });

    if (insertError) {
      setError(insertError.message);
    } else {
      setPlaceholderTitle("");
      setPlaceholderBody("");
      setMessage("Blank page added.");
      await loadSetlistWorkspace();
    }

    setSavingSetlistAction(false);
  }

  async function reorderSetlistItems(draggedItemId, targetItemId) {
    if (!isSelectedEventOwner || !draggedItemId || draggedItemId === targetItemId) {
      return;
    }

    const draggedIndex = selectedSetlistItems.findIndex(
      (setlistItem) => setlistItem.id === draggedItemId
    );
    const targetIndex = selectedSetlistItems.findIndex(
      (setlistItem) => setlistItem.id === targetItemId
    );

    if (draggedIndex === -1 || targetIndex === -1) {
      return;
    }

    const reorderedItems = [...selectedSetlistItems];
    const [draggedItem] = reorderedItems.splice(draggedIndex, 1);

    reorderedItems.splice(targetIndex, 0, draggedItem);
    setSavingSetlistAction(true);
    setError("");
    setMessage("");

    const updates = await Promise.all(
      reorderedItems.map((setlistItem, index) =>
        supabase
          .from("setlist_items")
          .update({ position: index })
          .eq("id", setlistItem.id)
      )
    );
    const updateError = updates.find((update) => update.error)?.error;

    if (updateError) {
      setError(updateError.message);
    } else {
      setSetlistItems((currentItems) => {
        const reorderedItemIds = new Set(
          reorderedItems.map((setlistItem) => setlistItem.id)
        );
        const untouchedItems = currentItems.filter(
          (setlistItem) => !reorderedItemIds.has(setlistItem.id)
        );

        return [
          ...untouchedItems,
          ...reorderedItems.map((setlistItem, index) => ({
            ...setlistItem,
            position: index,
          })),
        ];
      });
      await loadSetlistWorkspace();
    }

    setSavingSetlistAction(false);
  }

  async function deleteSetlistItem(item) {
    if (!selectedSetlist || selectedSetlist.owner_id !== user.id) return;
    setSavingSetlistAction(true);
    setError("");
    setMessage("");

    const { error: deleteError } = await supabase
      .from("setlist_items")
      .delete()
      .eq("id", item.id);

    if (deleteError) {
      setError(deleteError.message);
    } else {
      setMessage("Setlist item removed.");
      await loadSetlistWorkspace();
    }

    setSavingSetlistAction(false);
  }

  async function saveSetlistNotePage(event, item) {
    event.preventDefault();
    if (!selectedSetlist || selectedSetlist.owner_id !== user.id) return;

    const formData = new FormData(event.currentTarget);
    const title = String(formData.get("title") || "").trim();
    const body = String(formData.get("body") || "").trim();
    if (!title && !body) {
      setError("Add a title or text to the note page.");
      return;
    }

    setSavingSetlistAction(true);
    setError("");
    setMessage("");
    const { error: updateError } = await supabase
      .from("setlist_items")
      .update({ title: title || "Blank page", body })
      .eq("id", item.id);

    if (updateError) {
      setError(updateError.message);
    } else {
      setMessage("Note page updated.");
      await loadSetlistWorkspace();
    }
    setSavingSetlistAction(false);
  }

  async function deleteSetlist(setlist) {
    const deleteConfirmed = window.confirm(`Delete "${setlist.title}"?`);

    if (!deleteConfirmed) {
      return;
    }

    setSavingSetlistAction(true);
    setError("");
    setMessage("");

    const { error: deleteError } = await supabase
      .from("setlists")
      .delete()
      .eq("id", setlist.id);

    if (deleteError) {
      setError(deleteError.message);
    } else {
      if (selectedSetlistId === setlist.id) {
        setSelectedSetlistId(
          setlists.find((event) => event.id !== setlist.id)?.id || null
        );
      }
      setMessage("Setlist deleted.");
      await loadSetlistWorkspace();
    }

    setSavingSetlistAction(false);
  }

  async function downloadCombinedSetlistPdf(setlist) {
    const shouldUseSignedUrl = prefersPdfViewerWindow();
    const viewerWindow = openPdfDownloadWindow();

    setCombiningSetlistId(setlist.id);
    setError("");
    setMessage("");

    const { data, error: combineError } = await supabase.functions.invoke(
      "r2-song-files",
      {
        body: {
          action: "combined-setlist-pdf",
          preferSignedUrl: shouldUseSignedUrl,
          setlistId: setlist.id,
        },
      }
    );

    if (combineError) {
      viewerWindow?.close();
      setError(`Could not build the PDF: ${await getFunctionErrorMessage(combineError)}`);
      setCombiningSetlistId(null);
      return;
    }

    if (!data?.data) {
      if (data?.signedUrl) {
        openPdfUrl(data.signedUrl, viewerWindow);
        if (data.warnings?.length) {
          const warningNoun = data.warnings.length === 1 ? "song" : "songs";
          const warningVerb = data.warnings.length === 1 ? "was" : "were";

          setMessage(
            `Combined PDF opened. ${data.warnings.length} ${warningNoun} ${warningVerb} replaced with note pages.`
          );
        } else {
          setMessage("Combined setlist PDF opened.");
        }
        setCombiningSetlistId(null);
        return;
      }

      viewerWindow?.close();
      setError("Could not build the PDF: the server did not return a file.");
      setCombiningSetlistId(null);
      return;
    }

    downloadBase64Pdf(data.data, data.fileName, viewerWindow);
    if (data.warnings?.length) {
      const warningNoun = data.warnings.length === 1 ? "song" : "songs";
      const warningVerb = data.warnings.length === 1 ? "was" : "were";

      setMessage(
        `Combined PDF downloaded. ${data.warnings.length} ${warningNoun} ${warningVerb} replaced with note pages.`
      );
    } else {
      setMessage("Combined setlist PDF downloaded.");
    }
    setCombiningSetlistId(null);
  }

  async function copySetlistShareLink(setlist) {
    const shareUrl = `${window.location.origin}/setlists/${setlist.id}`;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setMessage("Friend-only setlist link copied.");
      setError("");
    } catch {
      setError("Could not copy the link. You can share this URL: " + shareUrl);
    }
  }

  function toggleSetlistFolder(folderId) {
    setOpenSetlistFolderIds((currentFolderIds) =>
      currentFolderIds.includes(folderId)
        ? currentFolderIds.filter((currentId) => currentId !== folderId)
        : [...currentFolderIds, folderId]
    );
  }

  function openSetlistFolder(folderId) {
    setCurrentSetlistFolderId(folderId);
    setSelectedSetlistId(null);
    setOpenSetlistFolderIds((currentFolderIds) =>
      currentFolderIds.includes(folderId)
        ? currentFolderIds
        : [...currentFolderIds, folderId]
    );
  }

  function renderSetlistTree(parentFolderId = null, depth = 0) {
    const nestedFolders = setlistFolders
      .filter((folder) => folder.parent_folder_id === parentFolderId)
      .sort((firstFolder, secondFolder) =>
        firstFolder.name.localeCompare(secondFolder.name)
      );
    const nestedSetlists = setlists
      .filter((setlist) => !setlist.is_event && setlist.folder_id === parentFolderId)
      .sort((firstSetlist, secondSetlist) => {
        if (firstSetlist.event_date && secondSetlist.event_date) {
          return new Date(secondSetlist.event_date) - new Date(firstSetlist.event_date);
        }

        if (firstSetlist.event_date) {
          return -1;
        }

        if (secondSetlist.event_date) {
          return 1;
        }

        return (firstSetlist.title || "").localeCompare(secondSetlist.title || "");
      });

    return (
      <>
        {nestedFolders.map((folder) => {
          const isOpen = openSetlistFolderIds.includes(folder.id);
          const childCount =
            setlistFolders.filter(
              (childFolder) => childFolder.parent_folder_id === folder.id
            ).length +
            setlists.filter((setlist) => !setlist.is_event && setlist.folder_id === folder.id).length;

          return (
            <div className="setlist-tree-group" key={folder.id}>
              <div
                className={`setlist-tree-row folder-row ${
                  currentSetlistFolderId === folder.id ? "active" : ""
                }`}
                style={{ "--tree-depth": depth }}
              >
                <button
                  aria-label={isOpen ? "Collapse folder" : "Open folder"}
                  className={`setlist-disclosure ${isOpen ? "open" : ""}`}
                  type="button"
                  onClick={() => toggleSetlistFolder(folder.id)}
                >
                  &gt;
                </button>
                <button type="button" onClick={() => openSetlistFolder(folder.id)}>
                  <span className="setlist-folder-icon" aria-hidden="true"></span>
                  <span>
                    <strong>{folder.name}</strong>
                    <small>
                      {childCount} {childCount === 1 ? "item" : "items"}
                    </small>
                  </span>
                </button>
                <button
                  aria-label={`Delete ${folder.name}`}
                  className="setlist-tree-delete"
                  disabled={savingSetlistAction}
                  type="button"
                  onClick={() => deleteSetlistFolder(folder)}
                >
                  Remove
                </button>
              </div>
              {isOpen && (
                <div className="setlist-tree-children">
                  {renderSetlistTree(folder.id, depth + 1)}
                </div>
              )}
            </div>
          );
        })}

        {nestedSetlists.map((setlist) => {
          const itemCount = setlistItems.filter(
            (item) => item.setlist_id === setlist.id
          ).length;

          return (
            <div
              className={`setlist-tree-row setlist-row ${
                selectedSetlist?.id === setlist.id ? "active" : ""
              }`}
              key={setlist.id}
              style={{ "--tree-depth": depth }}
            >
              <button
                type="button"
                onClick={() => {
                  setCurrentSetlistFolderId(setlist.folder_id);
                  setSelectedSetlistId(setlist.id);
                }}
              >
                <span className="setlist-row-lines" aria-hidden="true"></span>
                <span>
                  <strong>{setlist.title}</strong>
                  <small>
                    {itemCount} {itemCount === 1 ? "item" : "items"} ·{" "}
                    {formatSetlistDate(setlist.event_date)}
                  </small>
                </span>
                <span aria-hidden="true">&gt;</span>
              </button>
              <button
                aria-label={`Delete ${setlist.title}`}
                className="setlist-tree-delete"
                disabled={savingSetlistAction}
                type="button"
                onClick={() => deleteSetlist(setlist)}
              >
                Remove
              </button>
            </div>
          );
        })}
      </>
    );
  }

  return (
    <main className="page app-page library-page">
      <section className="library-shell">
        <aside className="library-sidebar" aria-label="Song library sections">
          <div className="library-brand-block">
            <p className="eyebrow">PDF library</p>
            <h1>
              {isEventsShelf
                ? "Events"
                : isSetlistsShelf
                ? "Setlists"
                : isMyShelf
                  ? "My Library"
                  : "Friends Library"}
            </h1>
            <p>
              {isEventsShelf
                ? "Plan events with your songs and team in one place."
                : isSetlistsShelf
                ? "Plan services with your songs and team in one place."
                : isMyShelf
                ? "Open your charts, organize your scores, and sort songs by title or key."
                : "Open charts shared by your accepted friends!"}
            </p>
          </div>

          <div className="library-shelves" aria-label="Library shelves">
            <button
              className={isMyShelf ? "active" : ""}
              onClick={() => setActiveShelf("mine")}
              type="button"
            >
              <span>My Library</span>
              <strong>{mySongsCount}</strong>
            </button>
            <button
              className={isFriendsShelf ? "active" : ""}
              onClick={() => setActiveShelf("friends")}
              type="button"
            >
              <span>Friends Library</span>
              <strong>{friendSongsCount}</strong>
            </button>
            <button
              className={isSetlistsShelf ? "active" : ""}
              onClick={() => setActiveShelf("setlists")}
              type="button"
            >
              <span>Setlists</span>
              <strong>{setlistsCount}</strong>
            </button>
            <button
              className={isEventsShelf ? "active" : ""}
              onClick={() => setActiveShelf("events")}
              type="button"
            >
              <span>Events</span>
              <strong>{eventCount}</strong>
            </button>
          </div>
        </aside>

        <section className="score-browser">
          {isSetlistsShelf ? (
            <div className="setlist-workspace">
              {message && <p className="form-message success">{message}</p>}
              {error && <p className="form-message error">{error}</p>}

              <div className="setlist-browser-grid">
                <aside className="setlist-folder-panel">
                  <div className="setlist-panel-heading">
                    <strong>Setlist Library</strong>
                    <div className="setlist-create-buttons">
                      <button
                        className={setlistCreateMode === "folder" ? "active" : ""}
                        type="button"
                        onClick={() =>
                          setSetlistCreateMode(
                            setlistCreateMode === "folder" ? "" : "folder"
                          )
                        }
                      >
                        Folder
                      </button>
                      <button
                        className={setlistCreateMode === "setlist" ? "active" : ""}
                        type="button"
                        onClick={() =>
                          setSetlistCreateMode(
                            setlistCreateMode === "setlist" ? "" : "setlist"
                          )
                        }
                      >
                        Setlist
                      </button>
                    </div>
                  </div>

                  <div className="setlist-control-grid">

                    {setlistCreateMode === "folder" && (
                      <form className="setlist-sidebar-form" onSubmit={createSetlistFolder}>
                        <p>
                          {currentSetlistFolder
                            ? `Adding to ${currentSetlistFolder.name}`
                            : "Adding to All setlists"}
                        </p>
                        <label>
                          Folder name
                          <input
                            onChange={(event) =>
                              setNewSetlistFolderName(event.target.value)
                            }
                            placeholder={
                              currentSetlistFolder
                                ? `Inside ${currentSetlistFolder.name}`
                                : "Steadfast"
                            }
                            type="text"
                            value={newSetlistFolderName}
                          />
                        </label>
                        <button
                          className="primary-button"
                          disabled={savingSetlistAction}
                          type="submit"
                        >
                          Create
                        </button>
                      </form>
                    )}

                    {setlistCreateMode === "setlist" && (
                      <form className="setlist-sidebar-form" onSubmit={createSetlist}>
                        <p>
                          {currentSetlistFolder
                            ? `Adding to ${currentSetlistFolder.name}`
                            : "Adding to All setlists"}
                        </p>
                        <label>
                          Title
                          <input
                            onChange={(event) =>
                              setNewSetlistTitle(event.target.value)
                            }
                            placeholder="Sunday Morning"
                            type="text"
                            value={newSetlistTitle}
                          />
                        </label>
                        <label>
                          Date
                          <input
                            onChange={(event) => setNewSetlistDate(event.target.value)}
                            type="date"
                            value={newSetlistDate}
                          />
                        </label>
                        <label className="setlist-form-wide">
                          Notes
                          <textarea
                            onChange={(event) => setNewSetlistNotes(event.target.value)}
                            placeholder="Optional notes"
                            rows="2"
                            value={newSetlistNotes}
                          />
                        </label>
                        <button
                          className="primary-button"
                          disabled={savingSetlistAction}
                          type="submit"
                        >
                          Create
                        </button>
                      </form>
                    )}
                  </div>

                  <div className="setlist-tree" aria-label="Setlist folders and setlists">
                    <button
                      className={`setlist-root-row ${
                        currentSetlistFolderId === null && !selectedSetlistId
                          ? "active"
                          : ""
                      }`}
                      type="button"
                      onClick={() => {
                        setCurrentSetlistFolderId(null);
                        setSelectedSetlistId(null);
                      }}
                    >
                      <span>All setlists</span>
                      <small>Choose a folder or add something new</small>
                    </button>
                    {setlistFolders.length === 0 && setlists.every((setlist) => setlist.is_event) ? (
                      <p className="song-resource-muted">No setlists yet.</p>
                    ) : (
                      renderSetlistTree()
                    )}
                  </div>
                </aside>

                <div className="setlist-detail-panel">
                  {selectedSetlist ? (
                    <>
                      <div className="setlist-detail-heading">
                        <div>
                          <p className="eyebrow">Setlist</p>
                          <h2>{selectedSetlist.title}</h2>
                          <span>{formatSetlistDate(selectedSetlist.event_date)}</span>
                        </div>
                        <div className="setlist-detail-actions">
                          <button
                            className="secondary-button"
                            type="button"
                            onClick={() => copySetlistShareLink(selectedSetlist)}
                          >
                            Share setlist link
                          </button>
                          <button
                            className="secondary-button"
                            disabled={combiningSetlistId === selectedSetlist.id}
                            type="button"
                            onClick={() => downloadCombinedSetlistPdf(selectedSetlist)}
                          >
                            {combiningSetlistId === selectedSetlist.id
                              ? "Building PDF..."
                              : "Download PDF"}
                          </button>
                        </div>
                      </div>

                      {selectedSetlist.notes && (
                        <p className="setlist-detail-notes">{selectedSetlist.notes}</p>
                      )}

                      <div className="setlist-item-tools">
                        <form className="setlist-song-picker" onSubmit={addSongToSetlist}>
                          <label>
                            Add a song
                            <select
                              onChange={(event) => setSetlistSongId(event.target.value)}
                              value={setlistSongId}
                            >
                              <option value="">Choose song</option>
                              {myLibrarySongs.map((song) => (
                                <option key={song.id} value={song.id}>
                                  {song.title} ({song.song_key || "No key"})
                                </option>
                              ))}
                            </select>
                          </label>
                          <button
                            className="primary-button"
                            disabled={savingSetlistAction || myLibrarySongs.length === 0}
                            type="submit"
                          >
                            Add song
                          </button>
                        </form>

                        <details className="setlist-add-menu">
                          <summary>Note page</summary>
                          <form
                            className="setlist-add-card"
                            onSubmit={addPlaceholderToSetlist}
                          >
                            <label>
                              Title
                              <input
                                onChange={(event) =>
                                  setPlaceholderTitle(event.target.value)
                                }
                                placeholder="Prayer"
                                type="text"
                                value={placeholderTitle}
                              />
                            </label>
                            <label>
                              Text
                              <textarea
                                onChange={(event) =>
                                  setPlaceholderBody(event.target.value)
                                }
                                placeholder="Prayer, Scripture, reading, or transition note"
                                rows="3"
                                value={placeholderBody}
                              />
                            </label>
                            <button
                              className="primary-button"
                              disabled={savingSetlistAction}
                              type="submit"
                            >
                              Add page
                            </button>
                          </form>
                        </details>
                      </div>

                      <div className="setlist-print-area">
                        <div className="setlist-print-heading">
                          <h1>{selectedSetlist.title}</h1>
                          <p>{formatSetlistDate(selectedSetlist.event_date)}</p>
                          {selectedSetlist.notes && <p>{selectedSetlist.notes}</p>}
                        </div>

                        {selectedSetlistItems.length === 0 ? (
                          <div className="library-empty-state">
                            <h3>No songs yet</h3>
                            <p>Add songs or note pages to build this setlist.</p>
                          </div>
                        ) : (
                          <ol className="setlist-item-list">
                            {selectedSetlistItems.map((item, index) => {
                              const itemSong = item.song_id
                                ? songsById[item.song_id]
                                : null;

                              return (
                                <li
                                  className={
                                    `${item.item_type === "placeholder"
                                      ? "placeholder-item"
                                      : ""} ${
                                      draggingSetlistItemId === item.id
                                        ? "dragging"
                                        : ""
                                    }`
                                  }
                                  draggable={!savingSetlistAction}
                                  key={item.id}
                                  onDragStart={(event) => {
                                    setDraggingSetlistItemId(item.id);
                                    event.dataTransfer.effectAllowed = "move";
                                    event.dataTransfer.setData("text/plain", item.id);
                                  }}
                                  onDragOver={(event) => {
                                    event.preventDefault();
                                    event.dataTransfer.dropEffect = "move";
                                  }}
                                  onDrop={(event) => {
                                    event.preventDefault();
                                    const draggedItemId =
                                      event.dataTransfer.getData("text/plain") ||
                                      draggingSetlistItemId;

                                    setDraggingSetlistItemId(null);
                                    reorderSetlistItems(draggedItemId, item.id);
                                  }}
                                  onDragEnd={() => setDraggingSetlistItemId(null)}
                                >
                                  <div className="setlist-item-main">
                                    <span>{index + 1}</span>
                                    <span
                                      aria-label="Drag to reorder"
                                      className="setlist-drag-handle"
                                      title="Drag to reorder"
                                    >
                                      <i></i>
                                      <i></i>
                                      <i></i>
                                    </span>
                                    <div>
                                      <strong>
                                        {item.item_type === "song"
                                          ? itemSong?.title || "Missing song"
                                          : item.title || "Blank page"}
                                      </strong>
                                      <p>
                                        {item.item_type === "song"
                                          ? `Key: ${itemSong?.song_key || "No key"}`
                                          : item.body || "Blank page"}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="setlist-item-actions">
                                    {itemSong && (
                                      <button
                                        type="button"
                                        onClick={() => openPdf(itemSong)}
                                      >
                                        Open
                                      </button>
                                    )}
                                    <button
                                      disabled={savingSetlistAction}
                                      type="button"
                                      onClick={() => deleteSetlistItem(item)}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                  {item.item_type === "placeholder" && selectedSetlist.owner_id === user.id && (
                                    <details className="event-song-editor">
                                      <summary>Edit note page</summary>
                                      <form onSubmit={(event) => saveSetlistNotePage(event, item)}>
                                        <label>Title<input defaultValue={item.title || ""} name="title" /></label>
                                        <label>Text<textarea defaultValue={item.body || ""} name="body" rows="4" /></label>
                                        <button className="secondary-button" disabled={savingSetlistAction} type="submit">Save note page</button>
                                      </form>
                                    </details>
                                  )}
                                </li>
                              );
                            })}
                          </ol>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="library-empty-state">
                      <h3>Choose a setlist</h3>
                      <p>Select one from the left, or create a new setlist to begin.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : isEventsShelf ? (
            <div className="setlist-workspace">
              {message && <p className="form-message success">{message}</p>}
              {error && <p className="form-message error">{error}</p>}

              <div className="setlist-browser-grid">
                <aside className="setlist-folder-panel">
                  <div className="setlist-panel-heading">
                    <strong>Events</strong>
                    <div className="setlist-create-buttons">
                      <button
                        className={setlistCreateMode === "setlist" ? "active" : ""}
                        type="button"
                        onClick={() =>
                          setSetlistCreateMode(
                            setlistCreateMode === "setlist" ? "" : "setlist"
                          )
                        }
                      >
                        New Event
                      </button>
                    </div>
                  </div>

                  <div className="setlist-control-grid">
                    {setlistCreateMode === "setlist" && (
                      <form className="setlist-sidebar-form" onSubmit={createSetlist}>
                        <label>
                          Event name
                          <input
                            onChange={(event) =>
                              setNewSetlistTitle(event.target.value)
                            }
                            placeholder="Sunday Service"
                            type="text"
                            value={newSetlistTitle}
                          />
                        </label>
                        <label>
                          Date
                          <input
                            onChange={(event) => setNewSetlistDate(event.target.value)}
                            type="date"
                            value={newSetlistDate}
                          />
                        </label>
                        <label className="setlist-form-wide">
                          Notes
                          <textarea
                            onChange={(event) => setNewSetlistNotes(event.target.value)}
                            placeholder="Optional notes"
                            rows="2"
                            value={newSetlistNotes}
                          />
                        </label>
                        <button
                          className="primary-button"
                          disabled={savingSetlistAction}
                          type="submit"
                        >
                          Create
                        </button>
                      </form>
                    )}
                  </div>

                  <div className="setlist-tree" aria-label="Setlists">
                    {currentFolderSetlists.length === 0 ? (
                      <p className="song-resource-muted">No setlists yet.</p>
                    ) : currentFolderSetlists.map((event) => (
                      <div
                        className={`setlist-tree-row setlist-row ${selectedSetlist?.id === event.id ? "active" : ""}`}
                        key={event.id}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedSetlistId(event.id);
                            setEventTab("overview");
                          }}
                        >
                          <span className="setlist-row-lines" aria-hidden="true"></span>
                          <span>
                            <strong>{event.title}</strong>
                            <small>{formatSetlistDate(event.event_date)} · {setlistItems.filter((item) => item.setlist_id === event.id).length} plan items</small>
                          </span>
                        </button>
                        {event.owner_id === user.id && (
                          <button
                            aria-label={`Delete ${event.title}`}
                            className="setlist-tree-delete"
                            disabled={savingSetlistAction}
                            type="button"
                            onClick={() => deleteSetlist(event)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </aside>

                <div className="setlist-detail-panel">
                  {selectedSetlist ? (
                    <>
                      <div className="setlist-detail-heading">
                        <div>
                          <p className="eyebrow">{isSelectedEventOwner ? "Your Event" : "You're on this team"}</p>
                          {editingEventTitle && isSelectedEventOwner ? (
                            <form className="event-title-edit" onSubmit={saveEventTitle}>
                              <input
                                aria-label="Event name"
                                autoFocus
                                onChange={(event) => setEventTitleDraft(event.target.value)}
                                value={eventTitleDraft}
                              />
                              <button className="primary-button" disabled={savingSetlistAction} type="submit">Save</button>
                              <button
                                className="text-button"
                                disabled={savingSetlistAction}
                                onClick={() => {
                                  setEventTitleDraft(selectedSetlist.title);
                                  setEditingEventTitle(false);
                                }}
                                type="button"
                              >
                                Cancel
                              </button>
                            </form>
                          ) : (
                            <div className="event-title-view">
                              <h2>{selectedSetlist.title}</h2>
                              {isSelectedEventOwner && (
                                <button className="text-button" onClick={() => setEditingEventTitle(true)} type="button">
                                  Edit name
                                </button>
                              )}
                            </div>
                          )}
                          <span>
                            {formatSetlistDate(selectedSetlist.event_date)}
                            {selectedSetlist.start_time ? ` · ${formatTime(selectedSetlist.start_time)}` : ""}
                            {selectedSetlist.location ? ` · ${selectedSetlist.location}` : ""}
                          </span>
                        </div>
                        <div className="setlist-detail-actions">
                          <button
                            className="secondary-button"
                            type="button"
                            onClick={() => copySetlistShareLink(selectedSetlist)}
                          >
                            Share event link
                          </button>
                          <button
                            className="secondary-button"
                            disabled={combiningSetlistId === selectedSetlist.id}
                            type="button"
                            onClick={() => downloadCombinedSetlistPdf(selectedSetlist)}
                          >
                            {combiningSetlistId === selectedSetlist.id
                              ? "Building PDF..."
                              : "Download PDF"}
                          </button>
                        </div>
                      </div>

                      <nav className="event-tabs" aria-label="Setlist sections">
                        {[
                          ["overview", "Overview"],
                          ["song-plan", "Song Plan"],
                          ["team", "Team & Instruments"],
                          ["themes", "Themes & Flow"],
                        ].map(([tab, label]) => (
                          <button
                            aria-current={eventTab === tab ? "page" : undefined}
                            className={eventTab === tab ? "active" : ""}
                            key={tab}
                            onClick={() => setEventTab(tab)}
                            type="button"
                          >
                            {label}
                          </button>
                        ))}
                      </nav>

                      {eventTab === "overview" && eventDetails && (
                        <form className="event-overview-form" onSubmit={saveEventDetails}>
                          <label>
                            Event name
                            {isSelectedEventOwner ? (
                              <input
                                onChange={(event) => setEventDetails((current) => ({ ...current, title: event.target.value }))}
                                required
                                value={eventDetails.title}
                              />
                            ) : (
                              <output className="event-readonly-value">{eventDetails.title || "—"}</output>
                            )}
                          </label>
                          <label>
                            Date
                            {isSelectedEventOwner ? (
                              <input
                                onChange={(event) => setEventDetails((current) => ({ ...current, event_date: event.target.value }))}
                                type="date"
                                value={eventDetails.event_date}
                              />
                            ) : (
                              <output className="event-readonly-value">
                                {formatSetlistDate(eventDetails.event_date) || "—"}
                              </output>
                            )}
                          </label>
                          <label>
                            Start time
                            {isSelectedEventOwner ? (
                              <input
                                onChange={(event) => setEventDetails((current) => ({ ...current, start_time: event.target.value }))}
                                type="time"
                                value={eventDetails.start_time}
                              />
                            ) : (
                              <output className="event-readonly-value">{formatTime(eventDetails.start_time) || "—"}</output>
                            )}
                          </label>
                          <label>
                            End time
                            {isSelectedEventOwner ? (
                              <input
                                onChange={(event) => setEventDetails((current) => ({ ...current, end_time: event.target.value }))}
                                type="time"
                                value={eventDetails.end_time}
                              />
                            ) : (
                              <output className="event-readonly-value">{formatTime(eventDetails.end_time) || "—"}</output>
                            )}
                          </label>
                          <label className="event-form-wide">
                            Location
                            {isSelectedEventOwner ? (
                              <textarea
                                className="event-location-field"
                                ref={eventLocationRef}
                                onChange={(event) => setEventDetails((current) => ({ ...current, location: event.target.value }))}
                                placeholder="Church / room"
                                rows="2"
                                value={eventDetails.location}
                              />
                            ) : (
                              <output className="event-readonly-value event-readonly-multiline">
                                {eventDetails.location || "—"}
                              </output>
                            )}
                          </label>
                          <label className="event-form-wide">
                            Event notes
                            {isSelectedEventOwner ? (
                              <textarea
                                className="event-notes-field"
                                ref={eventNotesRef}
                                onChange={(event) => setEventDetails((current) => ({ ...current, notes: event.target.value }))}
                                rows="4"
                                value={eventDetails.notes}
                              />
                            ) : (
                              <output className="event-readonly-value event-readonly-multiline">
                                {eventDetails.notes || "—"}
                              </output>
                            )}
                          </label>
                          {isSelectedEventOwner && (
                            <button className="primary-button event-form-wide" disabled={savingSetlistAction} type="submit">
                              Save overview
                            </button>
                          )}
                        </form>
                      )}

                      {eventTab === "team" && (
                        <section className="event-team-section">
                          <div className="event-section-heading">
                            <div>
                              <p className="eyebrow">Team & Instruments</p>
                              <h3>People serving this event</h3>
                            </div>
                            <span>{eventTeamMembers.length} {eventTeamMembers.length === 1 ? "person" : "people"}</span>
                          </div>
                          {eventTeamMembers.length === 0 ? (
                            <p className="library-empty-state">No friends have been added to this event yet.</p>
                          ) : (
                            <div className="event-team-list">
                              {eventTeamMembers.map((member) => (
                                <article className="event-team-card" key={member.id}>
                                  <div className="event-team-person">
                                    <strong>{member.display_name}</strong>
                                    <EventRoleChips roles={member.roles || []} />
                                  </div>
                                  <dl>
                                    {member.instrument_part && <div><dt>Instrument / Part</dt><dd>{member.instrument_part}</dd></div>}
                                    {member.needs_gear && <div><dt>Needs / Gear</dt><dd>{member.needs_gear}</dd></div>}
                                    {member.contact_notes && <div><dt>Contact / Notes</dt><dd>{member.contact_notes}</dd></div>}
                                  </dl>
                                  {isSelectedEventOwner && (
                                    <div className="event-team-card-actions">
                                      <details className="event-team-edit">
                                        <summary>Edit assignment</summary>
                                        <form onSubmit={(event) => saveEventTeamMember(event, member)}>
                                          <label>Name shown on this event<input defaultValue={member.name_override || ""} name="name_override" placeholder={member.display_name} /></label>
                                          <fieldset className="event-role-options">
                                            <legend>Role</legend>
                                            {eventRoleOptions.map((role) => (
                                              <label key={role}>
                                                <input defaultChecked={member.roles?.includes(role)} name="roles" type="checkbox" value={role} />
                                                {role}
                                              </label>
                                            ))}
                                          </fieldset>
                                          <label>Instrument / Part<input defaultValue={member.instrument_part} name="instrument_part" /></label>
                                          <label>Needs / Gear<input defaultValue={member.needs_gear} name="needs_gear" /></label>
                                          <label className="event-form-wide">Contact / Notes<textarea defaultValue={member.contact_notes} name="contact_notes" rows="3" /></label>
                                          <button className="secondary-button" disabled={savingSetlistAction} type="submit">Save assignment</button>
                                        </form>
                                      </details>
                                      <button className="subtle-danger-button" disabled={savingSetlistAction} onClick={() => removeEventTeamMember(member)} type="button">
                                        Remove
                                      </button>
                                    </div>
                                  )}
                                </article>
                              ))}
                            </div>
                          )}
                          {isSelectedEventOwner && (
                            <form className="event-team-form" onSubmit={addEventTeamMember}>
                              <h3 className="event-form-wide">Add a friend to the team</h3>
                              {acceptedFriends.length === 0 && (
                                <p className="event-form-wide">
                                  Add friends from <Link to="/friends">Friends</Link>, or add yourself to this event.
                                </p>
                              )}
                              <label className="event-form-wide">
                                Friend
                                <select onChange={(event) => setSelectedTeamFriendId(event.target.value)} required value={selectedTeamFriendId}>
                                  <option value="">Choose an accepted friend</option>
                                  {!isCurrentUserOnEventTeam && <option value={user.id}>Me (you)</option>}
                                  {availableEventFriends.map((friend) => <option key={friend.id} value={friend.id}>{friend.name}</option>)}
                                </select>
                              </label>
                              <label className="event-form-wide">
                                Name shown on this event
                                <input
                                  onChange={(event) => setTeamMemberForm((current) => ({ ...current, name_override: event.target.value }))}
                                  placeholder={acceptedFriends.find((friend) => friend.id === selectedTeamFriendId)?.name || "Use friend’s account name"}
                                  value={teamMemberForm.name_override}
                                />
                              </label>
                              <fieldset className="event-role-options event-form-wide">
                                <legend>Role</legend>
                                {eventRoleOptions.map((role) => (
                                  <label key={role}>
                                    <input
                                      checked={teamMemberForm.roles.includes(role)}
                                      onChange={(event) => setTeamMemberForm((current) => ({
                                        ...current,
                                        roles: event.target.checked
                                          ? [...current.roles, role]
                                          : current.roles.filter((currentRole) => currentRole !== role),
                                      }))}
                                      type="checkbox"
                                    />
                                    {role}
                                  </label>
                                ))}
                              </fieldset>
                              <label>
                                Instrument / Part
                                <input onChange={(event) => setTeamMemberForm((current) => ({ ...current, instrument_part: event.target.value }))} value={teamMemberForm.instrument_part} />
                              </label>
                              <label>
                                Needs / Gear
                                <input onChange={(event) => setTeamMemberForm((current) => ({ ...current, needs_gear: event.target.value }))} value={teamMemberForm.needs_gear} />
                              </label>
                              <label className="event-form-wide">
                                Contact / Notes
                                <textarea onChange={(event) => setTeamMemberForm((current) => ({ ...current, contact_notes: event.target.value }))} rows="3" value={teamMemberForm.contact_notes} />
                              </label>
                              <button className="primary-button event-form-wide" disabled={savingSetlistAction || (!availableEventFriends.length && isCurrentUserOnEventTeam)} type="submit">Add to team</button>
                            </form>
                          )}
                        </section>
                      )}

                      {eventTab === "themes" && eventDetails && (
                        <form className="event-overview-form event-themes-form" onSubmit={saveEventDetails}>
                          <label>
                            Theme / Scripture
                            <textarea disabled={!isSelectedEventOwner} onChange={(event) => setEventDetails((current) => ({ ...current, theme: event.target.value }))} rows="4" value={eventDetails.theme} />
                          </label>
                          <label>
                            Themes & Flow
                            <textarea disabled={!isSelectedEventOwner} onChange={(event) => setEventDetails((current) => ({ ...current, flow_notes: event.target.value }))} placeholder="Service flow, transitions, and themes" rows="8" value={eventDetails.flow_notes} />
                          </label>
                          {isSelectedEventOwner && <button className="primary-button" disabled={savingSetlistAction} type="submit">Save themes & flow</button>}
                        </form>
                      )}

                      {eventTab === "song-plan" && (
                        <>
                      <div className="setlist-item-tools">
                        {isSelectedEventOwner && <form className="setlist-song-picker" onSubmit={addSongToSetlist}>
                          <label>
                            Add a song
                            <select
                              onChange={(event) => setSetlistSongId(event.target.value)}
                              value={setlistSongId}
                            >
                              <option value="">Choose song</option>
                              {myLibrarySongs.map((song) => (
                                <option key={song.id} value={song.id}>
                                  {song.title} ({song.song_key || "No key"})
                                </option>
                              ))}
                            </select>
                          </label>
                          <button
                            className="primary-button"
                            disabled={savingSetlistAction || myLibrarySongs.length === 0}
                            type="submit"
                          >
                            Add song
                          </button>
                        </form>}

                        {isSelectedEventOwner && <details className="setlist-add-menu">
                          <summary>Note page</summary>
                          <form
                            className="setlist-add-card"
                            onSubmit={addPlaceholderToSetlist}
                          >
                            <label>
                              Title
                              <input
                                onChange={(event) =>
                                  setPlaceholderTitle(event.target.value)
                                }
                                placeholder="Prayer"
                                type="text"
                                value={placeholderTitle}
                              />
                            </label>
                            <label>
                              Text
                              <textarea
                                onChange={(event) =>
                                  setPlaceholderBody(event.target.value)
                                }
                                placeholder="Prayer, Scripture, reading, or transition note"
                                rows="3"
                                value={placeholderBody}
                              />
                            </label>
                            <button
                              className="primary-button"
                              disabled={savingSetlistAction}
                              type="submit"
                            >
                              Add page
                            </button>
                          </form>
                        </details>}
                      </div>

                      <div className="setlist-print-area">
                        <div className="setlist-print-heading">
                          <h1>{selectedSetlist.title}</h1>
                          <p>{formatSetlistDate(selectedSetlist.event_date)}</p>
                        </div>

                        {selectedSetlistItems.length === 0 ? (
                          <div className="library-empty-state">
                            <h3>No songs yet</h3>
                            <p>Add songs or note pages to build this setlist.</p>
                          </div>
                        ) : (
                          <ol className="setlist-item-list">
                            {selectedSetlistItems.map((item, index) => {
                              const itemSong = item.song_id
                                ? songsById[item.song_id]
                                : null;

                              return (
                                <li
                                  className={
                                    `${item.item_type === "placeholder"
                                      ? "placeholder-item"
                                      : ""} ${
                                      draggingSetlistItemId === item.id
                                        ? "dragging"
                                        : ""
                                    }`
                                  }
                                  draggable={!savingSetlistAction && isSelectedEventOwner}
                                  key={item.id}
                                  onDragStart={(event) => {
                                    setDraggingSetlistItemId(item.id);
                                    event.dataTransfer.effectAllowed = "move";
                                    event.dataTransfer.setData("text/plain", item.id);
                                  }}
                                  onDragOver={(event) => {
                                    event.preventDefault();
                                    event.dataTransfer.dropEffect = "move";
                                  }}
                                  onDrop={(event) => {
                                    event.preventDefault();
                                    const draggedItemId =
                                      event.dataTransfer.getData("text/plain") ||
                                      draggingSetlistItemId;

                                    setDraggingSetlistItemId(null);
                                    reorderSetlistItems(draggedItemId, item.id);
                                  }}
                                  onDragEnd={() => setDraggingSetlistItemId(null)}
                                >
                                  <div className="setlist-item-main">
                                    <span>{index + 1}</span>
                                    <span
                                      aria-label="Drag to reorder"
                                      className="setlist-drag-handle"
                                      title="Drag to reorder"
                                    >
                                      <i></i>
                                      <i></i>
                                      <i></i>
                                    </span>
                                    <div>
                                      <strong>
                                        {item.item_type === "song"
                                          ? itemSong?.title || item.title || "Missing song"
                                          : item.title || "Blank page"}
                                      </strong>
                                      {item.item_type === "song" ? (
                                        <span className="score-key">
                                          {itemSong?.song_key || item.song_key_snapshot || "No key"}
                                        </span>
                                      ) : (
                                        <p>{item.body || "Blank page"}</p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="setlist-item-actions">
                                    {itemSong && (
                                      <>
                                      <button
                                        type="button"
                                        aria-expanded={expandedEventResourceItemId === item.id}
                                        onClick={() => setExpandedEventResourceItemId(
                                          expandedEventResourceItemId === item.id ? null : item.id
                                        )}
                                      >
                                        Resources{(songResources[item.song_id] || []).length > 0 ? ` (${songResources[item.song_id].length})` : ""}
                                      </button>
                                      <button
                                        className="event-song-action-button event-song-open-button"
                                        type="button"
                                        onClick={() => openPdf(itemSong)}
                                      >
                                        Open
                                      </button>
                                      </>
                                    )}
                                    {isSelectedEventOwner && <button
                                      className="event-song-action-button"
                                      disabled={savingSetlistAction}
                                      type="button"
                                      onClick={() => deleteSetlistItem(item)}
                                    >
                                      Remove
                                    </button>}
                                  </div>
                                  {itemSong && expandedEventResourceItemId === item.id && (
                                    <div className="event-song-resources">
                                      {(songResources[item.song_id] || []).length === 0 ? (
                                        <p className="song-resource-muted">No resources yet.</p>
                                      ) : (
                                        <div className="song-resource-list">
                                          {songResources[item.song_id].map((resource) => (
                                            <article className="song-resource-card" key={resource.id}>
                                              <div className="song-resource-card-copy">
                                                <div>
                                                  <small>{resource.resource_type === "sheet" ? "Music sheet" : resource.resource_type}</small>
                                                  {resource.title && <h4>{resource.title}</h4>}
                                                  {resource.body && <p>{resource.body}</p>}
                                                  {resource.resource_type === "link" && resource.url && (
                                                    <a href={resource.url} rel="noopener noreferrer" target="_blank">Open link</a>
                                                  )}
                                                </div>
                                              </div>
                                              {resource.resource_type === "audio" && <SongResourceAudio resource={resource} />}
                                              {resource.resource_type === "sheet" && <SongResourceSheet resource={resource} />}
                                            </article>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  {item.item_type === "placeholder" && isSelectedEventOwner && (
                                    <details className="event-song-editor">
                                      <summary>Edit note page</summary>
                                      <form onSubmit={(event) => saveSetlistNotePage(event, item)}>
                                        <label>Title<input defaultValue={item.title || ""} name="title" /></label>
                                        <label>Text<textarea defaultValue={item.body || ""} name="body" rows="4" /></label>
                                        <button className="secondary-button" disabled={savingSetlistAction} type="submit">Save note page</button>
                                      </form>
                                    </details>
                                  )}
                                  {item.item_type === "song" && isSelectedEventOwner && (
                                    <details className="event-song-editor">
                                      <summary>Plan details</summary>
                                      <form onSubmit={(event) => saveEventSongDetails(event, item)}>
                                        {[
                                          ["artist_version", "Artist / Version"],
                                          ["tempo_feel", "Tempo / Feel"],
                                          ["theme_scripture", "Theme / Scripture"],
                                          ["lead_vocal", "Lead Vocal"],
                                          ["other_vocals", "Other Vocals"],
                                          ["arrangement", "Arrangement / Transitions"],
                                          ["status", "Status"],
                                          ["notes_link", "Notes / Link"],
                                        ].map(([field, label]) => (
                                          <label key={field}>
                                            {label}
                                            <input defaultValue={item[field] || (field === "status" ? "Confirmed" : "")} name={field} />
                                          </label>
                                        ))}
                                        <button className="secondary-button" disabled={savingSetlistAction} type="submit">Save plan details</button>
                                      </form>
                                    </details>
                                  )}
                                </li>
                              );
                            })}
                          </ol>
                        )}
                      </div>
                        </>
                      )}
                    </>
                  ) : (
                    <div className="library-empty-state">
                      <h3>Choose a setlist</h3>
                      <p>Select one from the left, or create a setlist to begin.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <>
          {isMyShelf && (
            <form className="upload-card upload-card-main form-stack" onSubmit={handleUpload}>
              <div className="upload-card-heading">
                <h2>Add Score</h2>
                <p>Add a PDF chart with a required song key.</p>
              </div>
              <label>
                Song title
                <input
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder="Amazing Grace"
                  type="text"
                  value={title}
                />
              </label>
              <label>
                Key
                <select
                  onChange={(event) => setSongKey(event.target.value)}
                  required
                  value={songKey}
                >
                  <option value="">Choose key</option>
                  {songKeyOptions.map((keyName) => (
                    <option key={keyName} value={keyName}>
                      {keyName}
                    </option>
                  ))}
                  <option value={customKeyValue}>Custom key</option>
                </select>
                {songKey === customKeyValue && (
                  <input
                    onChange={(event) => setCustomSongKey(event.target.value)}
                    placeholder="Example: C-D"
                    required
                    type="text"
                    value={customSongKey}
                  />
                )}
              </label>
              <label>
                PDF file
                <input
                  accept="application/pdf"
                  onChange={(event) => setFile(event.target.files?.[0] || null)}
                  required
                  type="file"
                />
              </label>
              <button className="primary-button" disabled={submitting} type="submit">
                {submitting ? "Uploading..." : "Upload score"}
              </button>
              {message && <p className="form-message success">{message}</p>}
              {error && <p className="form-message error">{error}</p>}
            </form>
          )}

          {!isMyShelf && error && <p className="form-message error">{error}</p>}

          {!isMyShelf && (
            <div className="friends-library-callout">
              <div>
                <strong>Need to add a friend?</strong>
                <p>Manage requests to unlock more shared songs.</p>
              </div>
              <Link to="/friends">Add friends</Link>
            </div>
          )}

          <div className={`library-toolbar ${isMyShelf ? "" : "friends-library-toolbar"}`}>
            <div className="library-count" aria-label="Library summary">
              <strong>{isMyShelf ? mySongsCount : selectedFriendSongsCount}</strong>
              <span>
                {(isMyShelf ? mySongsCount : selectedFriendSongsCount) === 1
                  ? "song"
                  : "songs"}
              </span>
            </div>
            {!isMyShelf && (
              <label>
                Friend
                <select
                  onChange={(event) => setFriendFilter(event.target.value)}
                  value={friendFilter}
                >
                  <option value="all">All friends</option>
                  {acceptedFriends.map((friend) => (
                    <option key={friend.id} value={friend.id}>
                      {friend.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label>
              Search
              <input
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={isMyShelf ? "Search by title" : "Search by title or friend"}
                type="search"
                value={searchQuery}
              />
            </label>
            <label>
              Sort
              <select onChange={(event) => setSortMode(event.target.value)} value={sortMode}>
                <option value="title">Alphabetical</option>
                <option value="key">Key: C to B</option>
                <option value="newest">Newest Added</option>
                <option value="oldest">Oldest Added</option>
              </select>
            </label>
          </div>

          {loadingSongs ? (
            <p className="empty-state">Loading songs...</p>
          ) : (isMyShelf ? mySongsCount : friendSongsCount) === 0 ? (
            <div className="library-empty-state">
              <h3>{isMyShelf ? "No scores yet" : "No friend scores yet"}</h3>
              <p>
                {isMyShelf
                  ? "Upload your first PDF chart from the Add Score panel."
                  : "Songs from accepted friends will appear here."}
              </p>
              {!isMyShelf && (
                <Link className="empty-state-action" to="/friends">
                  Add or manage friends
                </Link>
              )}
            </div>
          ) : visibleSongs.length === 0 ? (
            <div className="library-empty-state">
              <h3>No matching scores</h3>
              <p>Try a different search.</p>
            </div>
          ) : (
            <ul className="score-list">
              {visibleSongs.map((song) => {
                const isOwner = song.owner_id === user.id;
                const isAlreadyCopied = copiedSongSourceIds.has(song.id);
                const isExpanded = expandedSongId === song.id;
                const resources = songResources[song.id] || [];
                const resourceForm = getResourceForm(song.id);
                // Only one row can be edited at a time.
                const isEditing = editingSongId === song.id;

                return (
                  <li key={song.id}>
                    {isEditing ? (
                      <div className="score-edit-form">
                        <label>
                          Title
                          <input
                            onChange={(event) => setEditTitle(event.target.value)}
                            type="text"
                            value={editTitle}
                          />
                        </label>
                        <label>
                          Key
                          <select
                            onChange={(event) => setEditSongKey(event.target.value)}
                            value={editSongKey}
                          >
                            <option value="">Choose key</option>
                            {songKeyOptions.map((keyName) => (
                              <option key={keyName} value={keyName}>
                                {keyName}
                              </option>
                            ))}
                            <option value={customKeyValue}>Custom key</option>
                          </select>
                          {editSongKey === customKeyValue && (
                            <input
                              onChange={(event) =>
                                setCustomEditSongKey(event.target.value)
                              }
                              placeholder="Example: C-D"
                              required
                              type="text"
                              value={customEditSongKey}
                            />
                          )}
                        </label>
                      </div>
                    ) : (
                      <div className="score-open-button">
                        <span className="score-main">
                          <span className="score-title-line">
                            <button
                              className="score-title-button"
                              type="button"
                              onClick={() => openPdf(song)}
                            >
                              <strong>{song.title}</strong>
                            </button>
                            <button
                              aria-label={
                                isExpanded ? "Hide song resources" : "Show song resources"
                              }
                              className="score-resource-trigger"
                              onClick={() => setExpandedSongId(isExpanded ? null : song.id)}
                              title="Resources"
                              type="button"
                            >
                              <span></span>
                              <span></span>
                              <span></span>
                              {resources.length > 0 && (
                                <em>{resources.length}</em>
                              )}
                            </button>
                          </span>
                          <span>
                            {isOwner
                              ? "My library"
                              : friendsById[song.owner_id]?.name || "Friends library"}{" "}
                            · Added{" "}
                            {formatSongDate(song)}
                          </span>
                        </span>
                        <span className="score-key">{song.song_key || "No key"}</span>
                      </div>
                    )}

                    <div className="score-actions">
                      {isEditing ? (
                        <>
                          <button
                            type="button"
                            disabled={savingEdit}
                            onClick={() => saveSongEdit(song)}
                          >
                            {savingEdit ? "Saving..." : "Save"}
                          </button>
                          <button type="button" onClick={cancelEditingSong}>
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button type="button" onClick={() => openPdf(song)}>
                            Open
                          </button>
                          {isOwner && (
                            <>
                              <button
                                aria-label="Copy share link"
                                className="score-share-button"
                                title="Copy share link"
                                type="button"
                                onClick={() => copySongShareLink(song)}
                              >
                                <svg
                                  aria-hidden="true"
                                  fill="none"
                                  height="18"
                                  viewBox="0 0 24 24"
                                  width="18"
                                >
                                  <circle cx="18" cy="5" r="3" />
                                  <circle cx="6" cy="12" r="3" />
                                  <circle cx="18" cy="19" r="3" />
                                  <path d="m8.7 10.7 6.6-4.4m-6.6 7 6.6 4.4" />
                                </svg>
                              </button>
                              <button type="button" onClick={() => startEditingSong(song)}>
                                Edit
                              </button>
                              <button type="button" onClick={() => deleteSong(song)}>
                                Delete
                              </button>
                            </>
                          )}
                          {!isOwner && (
                            <button
                              disabled={copyingSongId === song.id || isAlreadyCopied}
                              type="button"
                              onClick={() => copySongToMyLibrary(song)}
                            >
                              {isAlreadyCopied
                                ? "Added to My Library"
                                : copyingSongId === song.id
                                  ? "Adding..."
                                  : "Add to My Library"}
                            </button>
                          )}
                        </>
                      )}
                    </div>

                    {isExpanded && (
                      <div className="song-resource-drawer">
                        <div className="song-resource-heading">
                          <div>
                            <strong>Attached Resources</strong>
                            <span>
                              {resources.length === 0
                                ? "No resources yet."
                                : `${resources.length} attached`}
                            </span>
                          </div>
                        </div>

                        {resources.length > 0 && (
                          <div className="song-resource-list">
                            {resources.map((resource) => (
                              <article className="song-resource-card" key={resource.id}>
                                <div className="song-resource-card-copy">
                                  <div>
                                    <small>{resource.resource_type === "sheet" ? "Music sheet" : resource.resource_type}</small>
                                    {resource.title && <h4>{resource.title}</h4>}
                                    {resource.body && <p>{resource.body}</p>}
                                    {resource.resource_type === "link" && resource.url && (
                                      <a
                                        href={resource.url}
                                        rel="noopener noreferrer"
                                        target="_blank"
                                      >
                                        Open link
                                      </a>
                                    )}
                                  </div>

                                </div>

                                {resource.resource_type === "audio" && (
                                  <SongResourceAudio resource={resource} />
                                )}
                                {(resource.resource_type === "sheet" || isOwner) && (
                                  <div className="song-resource-actions">
                                    {resource.resource_type === "sheet" && (
                                      <SongResourceSheet resource={resource} />
                                    )}
                                    {isOwner && (
                                      <button
                                        className="song-resource-delete"
                                        disabled={deletingResourceId === resource.id}
                                        type="button"
                                        onClick={() => deleteSongResource(resource)}
                                      >
                                        {deletingResourceId === resource.id ? "Deleting..." : "Delete"}
                                      </button>
                                    )}
                                  </div>
                                )}
                              </article>
                            ))}
                          </div>
                        )}

                        {isOwner && (
                          <form
                            className="song-resource-form"
                            onSubmit={(event) => {
                              event.preventDefault();
                              saveSongResource(song);
                            }}
                          >
                            <label>
                              Type
                              <select
                                onChange={(event) =>
                                  updateResourceForm(song.id, "type", event.target.value)
                                }
                                value={resourceForm.type}
                              >
                                <option value="note">Note</option>
                                <option value="link">Link</option>
                                <option value="audio">Audio demo</option>
                                <option value="sheet">Music sheet PDF</option>
                              </select>
                            </label>
                            <label>
                              Title
                              <input
                                onChange={(event) =>
                                  updateResourceForm(song.id, "title", event.target.value)
                                }
                                placeholder="Demo, arrangement note, tutorial..."
                                type="text"
                                value={resourceForm.title}
                              />
                            </label>

                            {resourceForm.type === "link" && (
                              <label>
                                Link
                                <input
                                  onChange={(event) =>
                                    updateResourceForm(song.id, "url", event.target.value)
                                  }
                                  placeholder="https://..."
                                  type="url"
                                  value={resourceForm.url}
                                />
                              </label>
                            )}

                            {resourceForm.type === "audio" && (
                              <label>
                                Audio file
                                <input
                                  accept="audio/*"
                                  onChange={(event) =>
                                    updateResourceForm(
                                      song.id,
                                      "audioFile",
                                      event.target.files?.[0] || null
                                    )
                                  }
                                  type="file"
                                />
                              </label>
                            )}

                            {resourceForm.type === "sheet" && (
                              <label>
                                PDF music sheet
                                <input
                                  accept="application/pdf,.pdf"
                                  onChange={(event) => updateResourceForm(
                                    song.id,
                                    "sheetFile",
                                    event.target.files?.[0] || null
                                  )}
                                  type="file"
                                />
                              </label>
                            )}

                            <label className="song-resource-form-wide">
                              Notes
                              <textarea
                                onChange={(event) =>
                                  updateResourceForm(song.id, "body", event.target.value)
                                }
                                placeholder="Optional notes for this resource"
                                rows="3"
                                value={resourceForm.body}
                              />
                            </label>

                            <button
                              className="secondary-button"
                              disabled={savingResourceSongId === song.id}
                              type="submit"
                            >
                              {savingResourceSongId === song.id
                                ? "Adding..."
                                : "Add resource"}
                            </button>
                          </form>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
            </>
          )}
        </section>
      </section>
    </main>
  );
}

export default Library;
